import { useCallback, useEffect, useState } from 'react';
import { CreditCard, Check, ArrowUpCircle } from 'lucide-react';
import { api, ErrorApi } from '../api';
import { useAuth } from '../lib/contextoAuth';
import CuentaYPagos from '../escala/CuentaYPagos';
import RecursosExtra from '../consola/RecursosExtra';
import BarraDeCuota from '../archivos/BarraDeCuota';
import { Cargando, ErrorConReintento } from '../ui/EstadoDePantalla';
import ModalConfirmar from '../ui/ModalConfirmar';
// PEAJE U-04 (parche de Peaje; el dueño del archivo es experiencia): el
// estado de la escalera, lo pactado y las acciones de la suscripción.
import TarjetaDeSuscripcion from '../peaje/TarjetaDeSuscripcion';

const pesos = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' });
// PEAJE U-04: el registro manual del pago no se ve (regla de la fachada:
// el cobro lo confirma la nube con el procesador). Se conserva el código
// para quien lo reabra con una decisión escrita.
const REGISTRO_MANUAL_VISIBLE = false;

export default function Suscripcion() {
  const { tieneNivel } = useAuth();
  const esDueno = tieneNivel('DUENO');
  const [sus, setSus] = useState(null);
  const [planes, setPlanes] = useState([]);
  const [pendiente, setPendiente] = useState(null); // pago por registrar
  const [msg, setMsg] = useState(null);
  const [error, setError] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  // PEAJE U-04 · vuelta 2 (N-4): UNA lista de planes en la pantalla. Con el
  // estado de la nube, la de la tarjeta (precios de la lista del nodo); la
  // de aquí abajo solo se ve si la tarjeta no lo tiene (null = aún no sabe).
  const [tarjetaConEstado, setTarjetaConEstado] = useState(null);
  // PEAJE U-04 · vuelta 2 (H-5): pasar a un plan sin costo es cancelar, y
  // pide la misma confirmación que «Cancelar» de la tarjeta.
  const [confirmarSinCosto, setConfirmarSinCosto] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const [s, p] = await Promise.all([api.get('/suscripcion'), api.get('/planes')]);
      setSus(s);
      setPlanes(p);
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // PEDIR, NO CAMBIAR. Desde la Fase G el plan lo aplica el proveedor
  // desde su consola y este servidor lo recoge en unos minutos. La
  // pantalla decia «Plan actualizado» y era mentira: lo unico que
  // pasaba era que quedaba pedido.
  async function pedir(clave) {
    setError(null); setMsg(null); setOcupado(true);
    try {
      const r = await api.post('/suscripcion/cambiar-plan', { plan: clave, ciclo: 'mensual' });
      setPendiente(r.pago || null);
      // PEAJE U-04: con un plan de pago, las `instrucciones` del servidor
      // mandan a «Registrar el pago» a mano y hablan de un cobro en línea
      // que no es el de hoy. El pago va por el enlace de «Pagos».
      setMsg(r.pago
        ? 'Queda pedido. El enlace para pagarlo aparece en «Pagos», más abajo; '
          + 'el plan se enciende al confirmarse el pago.'
        : (r.mensaje || r.instrucciones));
    } catch (e) {
      // PEAJE U-04: «Plan '…' no válido» (`pagos.plan_por_clave`) es el
      // servidor diciendo que ese plan ya no se ofrece. Se dice así, y la
      // lista se vuelve a leer.
      if (e instanceof ErrorApi && e.status === 404 && /^Plan '.*' no válido/.test(e.message)) {
        setError('Ese plan ya no se ofrece. La lista de planes se acaba de actualizar: elige uno de los que aparecen.');
        cargar();
        return;
      }
      setError(e instanceof ErrorApi ? e.message : 'No se pudo enviar la petición.');
    } finally { setOcupado(false); }
  }

  async function registrarPago() {
    setOcupado(true); setError(null);
    try {
      await api.post(`/suscripcion/pagos/${pendiente.id}/registrar`, {});
      // NI AQUI SE DICE «PLAN ACTIVO». Anotar el pago no activa nada:
      // lo confirma tu proveedor y baja despues.
      setPendiente(null);
      setMsg('Pago anotado. Se lo avisamos a tu proveedor de TEGO; en '
             + 'cuanto lo confirme, este servidor recogerá tu plan solo.');
      await cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo registrar el pago.');
    } finally { setOcupado(false); }
  }

  // LOS CUATRO ESTADOS (EXPERIENCIA E2): medida el 16 sep 2026, esta era la
  // única pantalla vacía y sin botón (41 caracteres). Ahora el error dice
  // las tres cosas y trae «Volver a intentarlo»; cargando enseña la forma.
  if (error && !sus) {
    return (
      <div className="max-w-2xl space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">Suscripción</h1>
        <ErrorConReintento que="No se pudo leer la suscripción" porque={error} onReintentar={cargar} />
      </div>
    );
  }
  if (!sus) {
    return (
      <div className="max-w-2xl space-y-3">
        <h1 className="text-xl font-semibold text-gray-900">Suscripción</h1>
        <Cargando filas={4} etiqueta="Cargando suscripción" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-4 space-y-4">
      {/* PEAJE U-04: el estado de la suscripción, arriba de todo. */}
      <TarjetaDeSuscripcion alLeerEstado={setTarjetaConEstado} />
      <div className="flex items-center gap-2">
        <CreditCard className="h-5 w-5 text-[var(--color-marca)]" />
        <span className="font-semibold text-gray-900">Suscripción</span>
        <span className="ml-auto text-xs font-semibold px-2 py-0.5 rounded bg-[var(--color-marca-suave)] text-[var(--color-marca)] uppercase">
          {sus.plan_nombre}
        </span>
      </div>

      <div className={`text-sm ${sus.vigente ? 'text-gray-600' : 'text-red-700 font-semibold'}`}>
        {/* PEAJE U-04: «la operación no se bloquea» dejó de ser cierto con
            la escalera (en consulta, lo que el plan inicial no trae se
            cierra). Lo que pasa y qué hacer lo dice la tarjeta de arriba. */}
        {sus.vigente
          ? `Vigente · ${sus.dias_restantes} día(s) restantes`
          : 'Vencida. Qué pasa y cómo pagar: «Tu suscripción», arriba.'}
      </div>

      <div className="space-y-2">
        <Uso label="Productos" usado={sus.uso.productos} limite={sus.limite_sku} />
        <Uso label="Usuarios" usado={sus.uso.usuarios} limite={sus.asientos_incluidos} />
        <Uso label="Sucursales" usado={sus.uso.sucursales} limite={sus.limite_sucursales} />
        {/* ARCHIVOS F (17 sep 2026, parche del integrador): «X de Y GB». */}
        <BarraDeCuota />
      </div>

      {/* AMBAR Y NO VERDE. Verde significa «ya está» en todo el sistema,
          y aquí lo único que hay es una petición anotada. */}
      {msg && <div className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded p-2.5">{msg}</div>}
      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2.5">{error}</div>}

      {/* PEAJE U-04: «Registrar pago (manual)» ya no se ofrece al cliente.
          El pago va por el enlace del procesador (bloque «Pagos») y lo
          confirma la nube; aquí queda solo el aviso de lo pendiente. */}
      {pendiente && esDueno && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 text-sm text-amber-900">
          Pago pendiente de {pesos.format(pendiente.monto)} ({pendiente.plan}). El enlace para pagarlo aparece en «Pagos», más abajo.
        </div>
      )}
      {REGISTRO_MANUAL_VISIBLE && pendiente && esDueno && (
        <div className="border border-amber-200 bg-amber-50 rounded-xl p-3 space-y-2">
          <div className="text-sm text-amber-900">
            Pago pendiente de {pesos.format(pendiente.monto)} ({pendiente.plan}).
          </div>
          <button
            onClick={registrarPago}
            disabled={ocupado}
            className="w-full bg-[var(--color-marca)] text-white font-semibold py-2.5 rounded-lg text-sm disabled:opacity-60"
          >
            Registrar pago (manual)
          </button>
          <p className="text-[11px] text-amber-700">
            El cobro en línea (Mercado Pago/Conekta) se habilitará con HTTPS.
          </p>
        </div>
      )}

      {esDueno && tarjetaConEstado === false && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-gray-500 uppercase">Pedir otro plan</div>
          {/* SE DICE ANTES DE PULSAR, no despues. Quien pulsa «Pedirlo»
              tiene que saber ya que no va a pasar nada al instante: un
              aviso que llega despues del clic es una sorpresa, no una
              explicacion. */}
          {/* PEAJE U-04 · vuelta 2 (H-5): el texto viejo (proveedor, sin cambios)
              dejó de ser cierto con la
              escalera. Se dice lo que pasa y dónde se ve. */}
          <p className="text-xs text-gray-600">
            Al pedirlo queda anotado con tu nombre y la fecha. Un plan de pago
            se enciende al confirmarse su pago; lo que rige y desde cuándo
            aparece arriba, en «Tu suscripción».
          </p>
          {planes.map((p) => {
            const actual = p.clave === sus.plan;
            return (
              <div key={p.clave} className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2">
                <div className="flex-1">
                  <div className="font-medium text-gray-900 text-sm">{p.nombre}</div>
                  <div className="text-xs text-gray-500">
                    {Number(p.precio_mensual) === 0 ? 'Gratis' : `${pesos.format(p.precio_mensual)}/mes`}
                    {' · '}{p.limite_sku ?? '∞'} SKU · {p.asientos_incluidos} usuarios
                  </div>
                </div>
                {actual ? (
                  <span className="text-xs font-semibold text-green-700 flex items-center gap-1">
                    <Check className="h-4 w-4" /> Actual
                  </span>
                ) : (
                  <button
                    onClick={() => (Number(p.precio_mensual) === 0 ? setConfirmarSinCosto(p) : pedir(p.clave))}
                    disabled={ocupado}
                    className="text-[var(--color-marca)] flex items-center gap-1 text-sm font-semibold disabled:opacity-60"
                  >
                    <ArrowUpCircle className="h-4 w-4" /> Pedirlo
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {confirmarSinCosto && (
        <ModalConfirmar
          titulo={`Pasar al plan ${confirmarSinCosto.nombre}`}
          descripcion={`Tu plan de pago se cancela y la empresa pasa al plan ${confirmarSinCosto.nombre} con todos sus datos. Lo que rige y desde cuándo aparece arriba, en «Tu suscripción», en cuanto TEGO Cloud lo confirma.`}
          etiquetaConfirmar={`Sí, pasar al plan ${confirmarSinCosto.nombre}`}
          peligro
          onConfirmar={() => pedir(confirmarSinCosto.clave)}
          onCerrar={() => setConfirmarSinCosto(null)}
        />
      )}

      {/* LO QUE LA CUENTA MANDA AL NODO · COBRO-CLIP B3 (carril escala,
          montado aquí por el integrador el 14 de septiembre de 2026): los
          módulos por cuenta, las líneas de pago, los recibos y el método
          guardado. Debajo del plan de siempre, que gana. */}
      <CuentaYPagos />
      {/* PEDIR MÁS RECURSOS · SUPREMO Parte B (carril consola, montado aquí
          por el integrador el 17 de septiembre de 2026): el catálogo con
          precio que bajó de la nube, la frase del Supremo y lo pedido. */}
      <RecursosExtra />
    </div>
  );
}

function Uso({ label, usado, limite }) {
  const pct = limite ? Math.min(100, Math.round((usado / limite) * 100)) : 0;
  const alto = limite && usado >= limite;
  return (
    <div>
      <div className="flex justify-between text-xs text-gray-600">
        <span>{label}</span>
        <span className="tabular-nums">{usado}{limite ? ` / ${limite}` : ' / ∞'}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded mt-1 overflow-hidden">
        <div
          className={`h-full rounded ${alto ? 'bg-red-500' : 'bg-[var(--color-marca)]'}`}
          style={{ width: limite ? `${pct}%` : '15%' }}
        />
      </div>
    </div>
  );
}
